/**
 * LibraryLinkModal — share a Library folder or file into the project.
 *
 * A miniature of the Library itself: the same accessible roots, the same
 * folders, browsed through the ordinary /vault endpoints — which are
 * permission-scoped per caller, so the tree a manager sees here is exactly
 * the tree they may link from. No parallel browse API to drift out of sync.
 *
 * Each folder shows its rollup summary and each file its own one-liner,
 * because that summary is precisely what the project's agents will receive:
 * linking carries the Library's descriptions across untouched, and choosing
 * what to share is easier when you can read what it claims to be.
 *
 * One pick at a time. Linking a folder shares everything directly inside it;
 * linking a file shares that file. The "use as context" choice rides on the
 * link, matching how agents attach Library items.
 */

import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslator } from '../hooks/useTranslator';
import { vaultService } from '@modules/vault/vaultService';
import type { VaultFolder, VaultFile } from '@modules/vault/types';
import projectService from '../services/projectService';
import { SkeletonRows, ErrorBlock } from './StateBlocks';
import './LibraryLinkModal.css';

interface LibraryLinkModalProps {
  isOpen: boolean;
  projectId: string;
  /** Already-linked node ids, so the list can say "already shared". */
  linkedFolderIds: Set<string>;
  linkedFileIds: Set<string>;
  onClose: () => void;
  /** Called after a successful link so the Files tab can refresh. */
  onLinked: () => void;
}

type Pick =
  | { kind: 'folder'; id: string; name: string }
  | { kind: 'file'; id: string; name: string };

export function LibraryLinkModal({
  isOpen, projectId, linkedFolderIds, linkedFileIds, onClose, onLinked,
}: LibraryLinkModalProps) {
  const { t, tRef } = useTranslator();
  const [folder, setFolder] = useState<VaultFolder | null>(null);
  const [roots, setRoots] = useState<VaultFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Pick | null>(null);
  const [asContext, setAsContext] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  /**
   * Search, because a tree is only browsable when you already know where
   * things live. Uses the Library's own /vault/search — permission-scoped
   * server-side, so a query can never surface something the caller could not
   * browse to. Two characters and a 300ms debounce, the same contract as the
   * Library's own search box.
   */
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<{
    folders: { folder_id: string; folder_name: string; tenant_name?: string | null }[];
    files: { file_id: string; folder_id: string; file_display_name: string; folder_name?: string | null }[];
    capped?: boolean;
  } | null>(null);
  const isSearchMode = query.trim().length >= 2;

  useEffect(() => {
    if (!isOpen) { setQuery(''); setResults(null); }
  }, [isOpen]);

  useEffect(() => {
    if (!isSearchMode) { setResults(null); return; }
    setSearching(true);
    const handle = window.setTimeout(() => {
      vaultService.search(query.trim())
        .then(setResults)
        .catch(() => setResults({ folders: [], files: [] }))
        .finally(() => setSearching(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [query, isSearchMode]);

  const loadRoots = useCallback(() => {
    setLoading(true);
    setError(null);
    setFolder(null);
    vaultService.listRootFolders()
      .then(setRoots)
      .catch(() => setError(
        tRef.current('projects.library.loadFailed') || t('projects.library.couldNotOpen'),
      ))
      .finally(() => setLoading(false));
  }, [tRef]);

  const openFolder = useCallback((folderId: string) => {
    setLoading(true);
    setError(null);
    vaultService.getFolder(folderId)
      .then(setFolder)
      .catch(() => setError(
        tRef.current('projects.library.loadFailed') || t('projects.library.couldNotOpen'),
      ))
      .finally(() => setLoading(false));
  }, [tRef]);

  useEffect(() => {
    if (!isOpen) return;
    setPicked(null);
    setAsContext(false);
    setLinkError(null);
    loadRoots();
  }, [isOpen, loadRoots]);

  if (!isOpen) return null;

  const handleLink = async () => {
    if (!picked) return;
    setLinking(true);
    setLinkError(null);
    try {
      await projectService.createLibraryLink(projectId, {
        ...(picked.kind === 'folder' ? { folder_id: picked.id } : { file_id: picked.id }),
        include_as_context: picked.kind === 'file' && asContext,
      });
      onLinked();
      onClose();
    } catch (err: any) {
      setLinkError(err?.response?.data?.error
        || (t('projects.library.linkFailed') || 'That item could not be linked.'));
    } finally {
      setLinking(false);
    }
  };

  const subfolders: VaultFolder[] = folder ? (folder.children || []) : roots;
  const files: VaultFile[] = folder?.files || [];
  const crumbs = folder ? [...(folder.ancestors || []), {
    folder_id: folder.folder_id, folder_name: folder.folder_name,
  }] : [];

  const summaryOf = (text?: string | null) => {
    const s = (text || '').replace(/\n/g, ' ').trim();
    return s.length > 140 ? `${s.slice(0, 140)}…` : s;
  };

  return createPortal(
    <div className="pj-overlay" onClick={onClose} role="presentation">
      <div
        className="pj-modal library-link-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('projects.library.title') || 'Link from the Library'}
      >
        <header className="pj-modal-header">
          <h2 className="pj-modal-title">
            {t('projects.library.title') || 'Link from the Library'}
          </h2>
          <button type="button" className="pj-icon-btn" onClick={onClose}
            aria-label={t('common.close') || 'Close'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div className="pj-modal-body library-link-body">
          <p className="library-link-lede">
            {t('projects.library.lede')
              || 'Pick a folder or a file. Its summary travels with it, and every agent in this project will be able to read it.'}
          </p>

          <div className="share-search library-link-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              dir="auto"
              className="share-search-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); } }}
              placeholder={t('projects.library.searchPlaceholder') || 'Search the whole Library…'}
              aria-label={t('projects.library.searchPlaceholder') || 'Search the whole Library'}
            />
          </div>

          {!isSearchMode && (
          <nav className="library-link-crumbs" aria-label={t('projects.library.path') || 'Path'}>
            <button type="button" className="library-link-crumb" onClick={loadRoots}>
              {t('projects.library.root') || 'Library'}
            </button>
            {crumbs.map(crumb => (
              <span key={crumb.folder_id} className="library-link-crumb-wrap">
                <span className="library-link-crumb-sep" aria-hidden="true">/</span>
                <button
                  type="button"
                  className="library-link-crumb"
                  onClick={() => openFolder(crumb.folder_id)}
                >
                  {crumb.folder_name}
                </button>
              </span>
            ))}
          </nav>
          )}

          {isSearchMode ? (
            searching && !results ? (
              <SkeletonRows rows={3} />
            ) : (
              <ul className="library-link-list">
                {results && results.folders.length === 0 && results.files.length === 0 && (
                  <li className="library-link-empty">
                    {(t('projects.library.searchNoResults') || 'Nothing matches \u201c{{q}}\u201d.')
                      .replace('{{q}}', query.trim())}
                  </li>
                )}
                {(results?.folders || []).map(hit => {
                  const already = linkedFolderIds.has(hit.folder_id);
                  const selected = picked?.kind === 'folder' && picked.id === hit.folder_id;
                  return (
                    <li key={hit.folder_id}>
                      <div className={`library-link-row ${selected ? 'is-selected' : ''}`}>
                        <button
                          type="button"
                          className="library-link-pick"
                          disabled={already}
                          aria-pressed={selected}
                          onClick={() => setPicked(selected ? null
                            : { kind: 'folder', id: hit.folder_id, name: hit.folder_name })}
                        >
                          <span className="library-link-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                            </svg>
                          </span>
                          <span className="library-link-text">
                            <span className="library-link-name" dir="auto">{hit.folder_name}</span>
                            {hit.tenant_name && (
                              <span className="library-link-summary" dir="auto">{hit.tenant_name}</span>
                            )}
                          </span>
                          {already && (
                            <span className="pj-pill pj-pill-neutral">
                              {t('projects.library.alreadyLinked') || 'Already shared'}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="pj-btn pj-btn-ghost pj-btn-sm library-link-open"
                          onClick={() => { setQuery(''); openFolder(hit.folder_id); }}
                        >
                          {t('projects.library.openFolder') || 'Open'}
                        </button>
                      </div>
                    </li>
                  );
                })}
                {(results?.files || []).map(hit => {
                  const already = linkedFileIds.has(hit.file_id);
                  const selected = picked?.kind === 'file' && picked.id === hit.file_id;
                  return (
                    <li key={hit.file_id}>
                      <div className={`library-link-row ${selected ? 'is-selected' : ''}`}>
                        <button
                          type="button"
                          className="library-link-pick"
                          disabled={already}
                          aria-pressed={selected}
                          onClick={() => setPicked(selected ? null
                            : { kind: 'file', id: hit.file_id, name: hit.file_display_name })}
                        >
                          <span className="library-link-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
                              <path d="M14 3v5h5" />
                            </svg>
                          </span>
                          <span className="library-link-text">
                            <span className="library-link-name" dir="auto">{hit.file_display_name}</span>
                            {hit.folder_name && (
                              <span className="library-link-summary">
                                {(t('projects.library.inFolder') || 'in {{folder}}')
                                  .replace('{{folder}}', hit.folder_name)}
                              </span>
                            )}
                          </span>
                          {already && (
                            <span className="pj-pill pj-pill-neutral">
                              {t('projects.library.alreadyLinked') || 'Already shared'}
                            </span>
                          )}
                        </button>
                      </div>
                    </li>
                  );
                })}
                {results?.capped && (
                  <li className="library-link-empty">
                    {t('projects.library.searchCapped')
                      || 'Showing the first matches \u2014 keep typing to narrow it down.'}
                  </li>
                )}
              </ul>
            )
          ) : loading ? (
            <SkeletonRows rows={4} />
          ) : error ? (
            <ErrorBlock message={error} onRetry={folder ? () => openFolder(folder.folder_id) : loadRoots} />
          ) : (
            <ul className="library-link-list">
              {subfolders.length === 0 && files.length === 0 && (
                <li className="library-link-empty">
                  {t('projects.library.emptyFolder') || 'Nothing in this folder.'}
                </li>
              )}
              {subfolders.map(sub => {
                const already = linkedFolderIds.has(sub.folder_id);
                const selected = picked?.kind === 'folder' && picked.id === sub.folder_id;
                return (
                  <li key={sub.folder_id}>
                    <div className={`library-link-row ${selected ? 'is-selected' : ''}`}>
                      <button
                        type="button"
                        className="library-link-pick"
                        disabled={already}
                        aria-pressed={selected}
                        onClick={() => setPicked(selected ? null
                          : { kind: 'folder', id: sub.folder_id, name: sub.folder_name })}
                      >
                        <span className="library-link-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
                          </svg>
                        </span>
                        <span className="library-link-text">
                          <span className="library-link-name" dir="auto">{sub.folder_name}</span>
                          {summaryOf(sub.rollup_summary) && (
                            <span className="library-link-summary">{summaryOf(sub.rollup_summary)}</span>
                          )}
                        </span>
                        {already && (
                          <span className="pj-pill pj-pill-neutral">
                            {t('projects.library.alreadyLinked') || 'Already shared'}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        className="pj-btn pj-btn-ghost pj-btn-sm library-link-open"
                        onClick={() => openFolder(sub.folder_id)}
                      >
                        {t('projects.library.openFolder') || 'Open'}
                      </button>
                    </div>
                  </li>
                );
              })}
              {files.map(file => {
                const already = linkedFileIds.has(file.file_id);
                const selected = picked?.kind === 'file' && picked.id === file.file_id;
                const fileSummary = summaryOf((file as any).summary);
                return (
                  <li key={file.vault_file_id}>
                    <div className={`library-link-row ${selected ? 'is-selected' : ''}`}>
                      <button
                        type="button"
                        className="library-link-pick"
                        disabled={already}
                        aria-pressed={selected}
                        onClick={() => setPicked(selected ? null
                          : { kind: 'file', id: file.file_id, name: file.file_display_name })}
                      >
                        <span className="library-link-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
                            <path d="M14 3v5h5" />
                          </svg>
                        </span>
                        <span className="library-link-text">
                          <span className="library-link-name" dir="auto">{file.file_display_name}</span>
                          {fileSummary && (
                            <span className="library-link-summary">{fileSummary}</span>
                          )}
                        </span>
                        {already && (
                          <span className="pj-pill pj-pill-neutral">
                            {t('projects.library.alreadyLinked') || 'Already shared'}
                          </span>
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {linkError && <div className="pj-form-error">{linkError}</div>}
        </div>

        <footer className="pj-modal-footer library-link-footer">
          {/* A FILE option only, matching agents' direct-context files (same
              per-file size cap). A whole folder in permanent context would
              inline an unbounded subtree into every turn. */}
          <label
            className={`library-link-context ${picked?.kind !== 'file' ? 'is-disabled' : ''}`}
            title={picked?.kind !== 'file'
              ? (t('projects.library.asContextFilesOnly')
                || 'Available when a single file is selected')
              : undefined}
          >
            <input
              type="checkbox"
              checked={picked?.kind === 'file' && asContext}
              disabled={picked?.kind !== 'file'}
              onChange={e => setAsContext(e.target.checked)}
            />
            <span>
              {t('projects.library.asContext') || 'Always include in the agent’s context'}
              {picked?.kind === 'folder' && (
                <em className="library-link-context-hint">
                  {t('projects.library.asContextFilesOnly')
                    || 'Available when a single file is selected'}
                </em>
              )}
            </span>
          </label>
          <div className="library-link-actions">
            <button type="button" className="pj-btn pj-btn-secondary" onClick={onClose}>
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="button"
              className="pj-btn pj-btn-primary"
              disabled={!picked || linking}
              onClick={handleLink}
            >
              {linking
                ? (t('projects.library.linking') || 'Linking…')
                : picked
                  ? (t('projects.library.linkNamed') || 'Link “{{name}}”').replace('{{name}}', picked.name)
                  : (t('projects.library.link') || 'Link')}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default LibraryLinkModal;
